'use client';

import { HermesPrewarmTrigger } from '@/components/flow/HermesPrewarmTrigger';
import { readSseStream } from '@/lib/sse';
import { useVoiceChatLoop } from '@/lib/use-voice-chat-loop';
import { useVoiceRecorder } from '@/lib/use-voice-recorder';
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Icon } from './Icon';

/**
 * V2.11 (2026-05-20) — LP-facing twin chat. Mirrors chat-shell's
 * authenticated experience (voice input + collapsed transcript blocks
 * for voice-origin user messages + SSE streaming) but talks to a
 * specific twin by slug instead of inferring the caller's own twin.
 *
 * Stage 1 (this commit): single conversation per visit, persisted in
 * the conversations table so the LP can leave + come back. Anonymous
 * visitors still get the original PublicChat (different path / no
 * persistence).
 *
 * Stage 2 (follow-up): pair with a sidebar rail listing the LP's
 * conversations across all twins, multi-conversation per twin.
 */

type ChatMessage = {
  /** 2026-06-16 — message id is set on rows hydrated from server (so
   *  failed-turn retry + future-edit affordances can identify the row). */
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  message_id?: string;
  from_voice?: boolean;
  /** V2.11 (2026-05-23) — drives the 「正在思考…(N 秒)」 indicator
   *  before the first delta arrives. Same field name used by
   *  chat-shell and TwinChatPanel. */
  streaming_started_at?: number;
  /** 2026-06-16 — set on hydrated rows where the prior streaming
   *  response never produced output. Renders as a quiet "stuck" turn
   *  (no thinking spinner) so users see the prior history is intact;
   *  retry_text holds the preceding user message so the user can
   *  type "重发" trivially. */
  failed?: boolean;
  retry_text?: string;
  /** Amy CRO-132 P1-2 (2026-06-29) — evidence drawer data for public
   *  twin. Same shape as owner-preview memory_used SSE event but with
   *  only the fields the visitor needs (no debug-only score/cross-domain). */
  memory?: PublicMemory | null;
};

type PublicMemory = {
  concepts: Array<{ slug: string; display_name: string; origin?: string | null }>;
  skills: Array<{ name: string; description: string }>;
  vault_passages: Array<{
    title: string;
    publisher?: string | null;
    date?: string | null;
  }>;
  external_sources: Array<{ name: string; provider?: string | null; date?: string | null }>;
};

/**
 * Amy CRO-132 P1-2 (2026-06-29) — citation token regex. The hermes
 * prompt instructs the LLM to emit `[F1] [C2] [V3] [T4]` markers next
 * to claims. On owner-preview we render these as clickable footnotes
 * scrolling to the evidence drawer; on the public twin we strip them
 * out (visitor never sees the drawer rows directly — they get the
 * collapsed summary chip).
 */
const CITATION_RE = /\[([FCVT])(\d{1,3})\]/g;
function stripCitations(text: string): string {
  return text.replace(CITATION_RE, '');
}

/**
 * R77-03 (Codex Round 77 recheck, 2026-06-29) — server-side prompt should
 * forbid these tech-leak phrasings, but LLMs occasionally still emit them.
 * Public visitors must NEVER see "API 配置问题" / "工具调用失败" / "技术错误"
 * style copy — they expose backend internals and break the "this is the
 * economist talking" illusion. Last-mile client-side scrubber: each
 * forbidden phrase maps to a neutral user-facing replacement that the
 * prompt allows.
 *
 * We pattern-match conservatively (Chinese only, full-phrase) to avoid
 * accidentally scrubbing legitimate text that contains "工具" in a
 * non-technical context. The scrub is idempotent — re-running on already-
 * clean text is a no-op.
 */
const TECH_LEAK_REPLACEMENTS: Array<[RegExp, string]> = [
  [/工具调用(遇到|失败|出现|发生)?(技术问题|技术错误|内部错误|异常)/g, '这条数据我现在没法实时拉'],
  [/工具调用失败\s*[·•・|]\s*技术错误/g, '这条数据我现在没法实时拉'],
  [
    /(API|api|接口|endpoint).{0,4}(配置|限制|限流|超时|失败|错误|异常|问题)/g,
    '数据源现在有点不稳定',
  ],
  [/(请求|调用).{0,4}(被限流|限流|过于频繁|频繁|超时)/g, '数据源现在有点不稳定'],
  [/(后端|服务|backend|系统|内部).{0,4}(错误|失败|异常|问题)/g, '数据源现在有点不稳定'],
  [/技术(错误|问题|异常|限制)/g, '现在不太稳定'],
  // R81-04 (Codex Round 81 smoke, 2026-06-30) — new tech-leak variants.
  [/工具.{0,4}(都)?卡住/g, '这条数据我现在没法实时拉'],
  [/(数据)?接口.{0,4}(卡|挂|连不上|拉不到)/g, '数据源现在有点不稳定'],
  [/(拉取|读取|获取).{0,4}(失败|异常|超时)/g, '这条数据我现在没法实时拉'],
];
function stripTechLeak(text: string): string {
  let out = text;
  for (const [re, replacement] of TECH_LEAK_REPLACEMENTS) {
    out = out.replace(re, replacement);
  }
  return out;
}

/**
 * Amy CRO-132 P2-8 (2026-06-29) — unsourced realtime-quote detector
 * mirrored from TwinChatPanel. Public-twin visitors see this chip when
 * the answer contains a price / yield / index / range pattern but
 * memory_used had 0 vault_passages (= no real-time tool fired). The
 * goal: visitor doesn't trust an inferred 「7.20-7.30」 as if Hermes
 * had pulled live data.
 */
const REALTIME_QUOTE_PATTERNS = [
  /\[实时\]/,
  /[$￥€¥£]\s*\d+(?:\.\d+)?/,
  /\d+(?:\.\d+)?\s*(?:美元|人民币|日元|欧元|港元)\s*\/\s*(?:桶|盎司|吨|股)/,
  /(?:现报|最新|今日|当前|今早|今晚)\s*[¥$￥]?\s*\d+(?:\.\d+)?/,
  /\d+(?:\.\d+)?\s*[-—~至]\s*\d+(?:\.\d+)?\s*(?:美元|元|欧元|港元|%|％|个百分点|bp|BP)/,
  /(?:Brent|WTI|HKD|CNY|USD|EUR|JPY|GBP|S&?P|纳指|道指|恒生|国债|收益率|汇率)[^\n]{0,30}?\d+(?:\.\d+)?/,
];
function detectUnsourcedRealtimeQuote(
  content: string,
  memory: PublicMemory | null | undefined,
): boolean {
  if (!content) return false;
  // Only a successful external tool call can support a live quote. Vault
  // passages may be useful context, but they do not prove that a current
  // market/data lookup happened in this turn.
  if ((memory?.external_sources?.length ?? 0) > 0) return false;
  return REALTIME_QUOTE_PATTERNS.some((re) => re.test(content));
}

/**
 * Amy CRO-132 A14 (2026-06-29) — boundary refusal detector for public
 * twin (mirrored from TwinChatPanel). When the economist's SOUL is set
 * to `when_unsure='refuse'` and the model declines to answer, surface
 * a clear 🛑 pill so the visitor sees the framing match the content
 * (instead of getting evidence chips that look like the model
 * answered substantively).
 */
// R75-02 (Codex Round 75 recheck, 2026-06-29) — expand patterns to catch
// the boundary refusals the LLM actually produces. Codex's test refusal
// 「这不是我的专业范围…对流行文化趋势没有跟踪也没有分析框架」 missed all
// 6 original patterns; the public evidence strip kept rendering instead
// of the 🛑 「边界拒答 · 未使用证据」 pill.
const REFUSAL_PATTERNS = [
  /不在.{0,12}(覆盖|研究|讨论|专业).{0,8}范围/,
  /不.{0,4}研究.{0,4}(这|那)/,
  /没.{0,6}(专门|系统).{0,4}(跟|研究|看)/,
  /(完全|直接)?拒答/,
  /超出.{0,8}(覆盖|讨论|分析).{0,4}范围/,
  /(我|本人).{0,4}(不|无法).{0,4}(给|做|提供).{0,8}(具体|准确).{0,4}(预测|判断|建议)/,
  // CRO-134 + R75-02 — soft boundary phrasings (「这不是我的专业范围」/
  // 「不是我的活儿」/「评价人不是我的强项」/「没有跟踪 / 关注」).
  /不是.{0,8}(我|本人).{0,4}(的)?.{0,4}(专业|研究|领域|覆盖)/,
  /不是.{0,8}(我|本人).{0,4}(的)?.{0,6}(活|强项|本行|擅长|专长)/,
  /(没|没有).{0,8}(跟踪|关注|追|研究|分析|看过)/,
  /评价.{0,4}(人|同行|公司|政策).{0,8}不是.{0,4}(我|本人).{0,4}强项/,
  // CRO-134 §4 越界/无关 — 「这个不是我的活儿」「我也就对 X 有点心得」
  /(也就|只).{0,4}对.{0,8}(经济|市场|这块|这条|宏观).{0,8}心得/,
  /不能.{0,6}(替你|帮你).{0,6}(拍板|决定|下单)/,
];
function isBoundaryRefusal(content: string): boolean {
  if (!content) return false;
  const trimmed = content.trim();
  const firstPara = trimmed.split(/\n{2,}/, 1)[0]?.slice(0, 240) ?? trimmed.slice(0, 240);
  return REFUSAL_PATTERNS.some((re) => re.test(firstPara));
}

type Economist = { id: string; slug: string; display_name: string };

export function TwinChatLp({
  userId,
  economist,
  isOwner,
}: {
  userId: string;
  economist: Economist;
  /**
   * AXQA-20260618-03 — when the signed-in user owns this twin, the
   * conversation is a 「launch preview」 — their own preview of what a
   * visitor would see. When they don't, it's a real 「public_visitor」
   * session. Both buckets stay separated from /studio/test-chat
   * (scope='test_chat') so the owner's QA failures don't bleed onto
   * the public surface and visitor turns don't pollute test-chat history.
   */
  isOwner?: boolean;
}): JSX.Element {
  const scope = isOwner ? 'launch_preview' : 'public_visitor';
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 2026-06-16 — hydrate the latest conversation with this twin so
  // navigating away and back (or re-entering from /discover) keeps the
  // prior thread visible. Mirrors the TwinChatPage hydration done for
  // owner-preview; without this, every visit to /e/{slug} started blank
  // even when the orchestrator had the full history.
  //
  // Broken-turn cleanup matches TwinChatPage: an assistant row with
  // streaming=false and empty content (server flips is_streaming → 0
  // after 60s) is rendered as a "回答失败 · 重试" affordance instead of
  // a blank bubble, with retry_text wired from the immediately prior
  // user turn so a single click resends.
  useEffect(() => {
    if (!userId || !economist.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/conversations/latest?economist_id=${encodeURIComponent(economist.id)}&scope=${scope}`,
          { credentials: 'include', cache: 'no-store' },
        );
        if (!r.ok) return;
        const j = (await r.json()) as {
          id: string | null;
          messages: {
            id: string;
            role: 'user' | 'assistant' | 'system';
            content: string;
            is_streaming?: boolean;
          }[];
        };
        if (cancelled || !j.id) return;
        const hydrated: ChatMessage[] = (j.messages ?? [])
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => {
            const streaming = !!m.is_streaming;
            const trimmed = (m.content ?? '').trim();
            const isAssistant = m.role === 'assistant';
            const failed = isAssistant && !streaming && trimmed.length === 0;
            return {
              id: m.id,
              role: m.role as 'user' | 'assistant',
              content: m.content,
              streaming,
              failed,
            } as ChatMessage;
          });
        // AXQA-42-02 (2026-06-26) — drop failed assistant turns + their
        // immediately-prior user turn from the initial history hydrate.
        // Before: opening /e/<slug> showed every failed test prompt with
        // a 「⚠ AI 没回复 ↻ 重试」 pill, so a visitor's first impression
        // was a wall of QA-round failure pairs. Now we suppress the
        // failed pair entirely on hydrate (retry is still available if
        // the user manually re-asks). Keeps the bubble flow clean.
        const cleaned: ChatMessage[] = [];
        const dropFailedIdx = new Set<number>();
        for (let i = 0; i < hydrated.length; i++) {
          const cur = hydrated[i];
          if (!cur) continue;
          if (cur.role === 'assistant' && cur.failed) {
            dropFailedIdx.add(i);
            if (i > 0 && hydrated[i - 1]?.role === 'user') {
              dropFailedIdx.add(i - 1);
            }
          }
        }
        for (let i = 0; i < hydrated.length; i++) {
          if (!dropFailedIdx.has(i) && hydrated[i]) {
            cleaned.push(hydrated[i] as ChatMessage);
          }
        }
        setConversationId(j.id);
        setMessages(cleaned);
      } catch {
        // network blip — chat still works, just starts fresh
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, economist.id, scope]);
  // WeChat-style mode toggle: text mode keeps the keyboard up, voice
  // mode shows the mic. Same pattern as /studio chat-shell so the LP
  // experience matches what the economist sees during training.
  type InputMode = 'text' | 'voice';
  const [inputMode, setInputMode] = useState<InputMode>('text');

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-scroll on every messages change (incl. streaming deltas).
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every messages mutation
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = useCallback(
    async (
      text: string,
      opts: { fromVoice?: boolean; skipPushUser?: boolean; signal?: AbortSignal } = {},
    ) => {
      if (!text) return;
      setLoading(true);
      setError(null);

      // Lazy-create a conversation on first send. Subsequent messages
      // reuse the same convId so server-side rate limits + history
      // ordering apply correctly.
      let convId = conversationId;
      try {
        if (!convId) {
          const res = await fetch('/api/conversations', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ user_id: userId, economist_id: economist.id, scope }),
          });
          if (!res.ok) throw new Error(`create_conversation ${res.status}`);
          const j = (await res.json()) as { id: string };
          convId = j.id;
          setConversationId(convId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '无法创建对话');
        setLoading(false);
        return;
      }

      // Optimistic append: user bubble + streaming-assistant placeholder.
      // Voice mode already inserted the user bubble during transcription
      // (see useVoiceChatLoop's onPartial / onFinal wiring) — skipPushUser
      // avoids duplicating it.
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: '',
        streaming: true,
        streaming_started_at: Date.now(),
      };
      if (opts.skipPushUser) {
        setMessages((prev) => [...prev, assistantMsg]);
      } else {
        const userMsg: ChatMessage = { role: 'user', content: text, from_voice: opts.fromVoice };
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
      }

      try {
        const res = await fetch(`/api/conversations/${convId}/messages`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ content: text, from_voice: opts.fromVoice ?? false }),
          signal: opts.signal,
        });
        if (!res.ok || !res.body) {
          // 2026-06-19 — friendly toast for daily LLM cost cap.
          if (res.status === 429) {
            const txt = await res.text().catch(() => '');
            try {
              const j = JSON.parse(txt) as { hint?: string };
              throw new Error(j.hint || '今日额度已用完，请明天再试');
            } catch {
              throw new Error('今日额度已用完，请明天再试');
            }
          }
          throw new Error(`stream ${res.status}`);
        }

        await readSseStream(res.body, (ev) => {
          if (
            ev.type === 'message_id' &&
            typeof (ev as unknown as { id?: unknown }).id === 'string'
          ) {
            const messageId = (ev as unknown as { id: string }).id;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, message_id: messageId };
              }
              return next;
            });
          } else if (ev.type === 'chat_meta') {
            // AXQA-20260618-20/21 — same console marker as TwinChatPanel
            // so QA can confirm /e/{slug} owner-preview also picks up
            // the latest SOUL + Hermes build. 2026-06-19 — flat
            // stringified line + structured event so headless DOM
            // scrapers can read hermes_build without object expansion.
            if (typeof console !== 'undefined') {
              const m = ev as unknown as {
                soul_version?: number | null;
                tone_preset?: string | null;
                when_unsure?: string | null;
                hermes_build?: string | null;
                scope?: string | null;
              };
              console.warn(
                `[chat-meta] soul_version=${m.soul_version ?? 'null'} tone_preset=${m.tone_preset ?? 'null'} when_unsure=${m.when_unsure ?? 'null'} hermes_build=${m.hermes_build ?? 'null'} scope=${m.scope ?? 'null'}`,
              );
              console.warn('[chat-meta]', ev);
            }
          } else if (ev.type === 'memory_used') {
            // Amy CRO-132 P1-2 (2026-06-29) — attach evidence to the
            // latest assistant bubble so the visitor sees a 「N 概念 ·
            // M 框架 · K 原文」 summary chip below the answer instead
            // of a wall of unattributed prose.
            const m = ev as unknown as {
              concepts?: PublicMemory['concepts'];
              skills?: PublicMemory['skills'];
              vault_passages?: PublicMemory['vault_passages'];
            };
            const memory: PublicMemory = {
              concepts: m.concepts ?? [],
              skills: m.skills ?? [],
              vault_passages: m.vault_passages ?? [],
              external_sources: [],
            };
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, memory };
              }
              return next;
            });
          } else if (ev.type === 'evidence_final') {
            const evidence = ev as unknown as Partial<PublicMemory>;
            const memory: PublicMemory = {
              concepts: evidence.concepts ?? [],
              skills: evidence.skills ?? [],
              vault_passages: evidence.vault_passages ?? [],
              external_sources: evidence.external_sources ?? [],
            };
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last?.role === 'assistant') next[next.length - 1] = { ...last, memory };
              return next;
            });
          } else if (ev.type === 'tool_result' && typeof ev.name === 'string') {
            const toolName = ev.name;
            const result = ev.result as Record<string, unknown> | null | undefined;
            if (
              !toolName.startsWith('frameworks__') &&
              result &&
              typeof result === 'object' &&
              !('error' in result)
            ) {
              const provider =
                typeof result.source === 'string'
                  ? result.source
                  : typeof result.provider === 'string'
                    ? result.provider
                    : null;
              const date =
                typeof result.latest_observation_date === 'string'
                  ? result.latest_observation_date
                  : typeof result.retrieved_at_utc === 'string'
                    ? result.retrieved_at_utc
                    : null;
              setMessages((prev) => {
                const next = prev.slice();
                const last = next[next.length - 1];
                if (!last || last.role !== 'assistant') return prev;
                const memory = last.memory ?? {
                  concepts: [],
                  skills: [],
                  vault_passages: [],
                  external_sources: [],
                };
                const external = memory.external_sources ?? [];
                if (external.some((source) => source.name === toolName)) return prev;
                next[next.length - 1] = {
                  ...last,
                  memory: {
                    ...memory,
                    external_sources: [...external, { name: toolName, provider, date }],
                  },
                };
                return next;
              });
            }
          } else if (ev.type === 'delta' && typeof ev.text === 'string') {
            const chunk = ev.text;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, content: last.content + chunk };
              }
              return next;
            });
          } else if (ev.type === 'replace_content' && typeof ev.text === 'string') {
            // R89 final — server scrubbed fake blockquote / overclaim.
            const cleaned = ev.text;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, content: cleaned };
              }
              return next;
            });
          } else if (ev.type === 'replace_meta') {
            // R89 wave 5 recheck — public visitor DOES render evidence
            // chip (PublicEvidenceStrip). Field name here is `memory`,
            // not `memory_used`. Clear on server-side boundary decision.
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = {
                  ...last,
                  memory: { concepts: [], skills: [], vault_passages: [], external_sources: [] },
                };
              }
              return next;
            });
          } else if (ev.type === 'done') {
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, streaming: false };
              }
              return next;
            });
          } else if (ev.type === 'error') {
            const msg = typeof ev.message === 'string' ? ev.message : '回复时遇到上游错误。';
            setError(msg);
          }
        });

        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
        setInput('');
      } catch (err) {
        setError(err instanceof Error ? err.message : '回复加载失败');
        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              streaming: false,
              content: last.content || '⚠ 回复加载失败',
            };
          }
          return next;
        });
      } finally {
        setLoading(false);
        if (inputMode === 'text') requestAnimationFrame(() => textareaRef.current?.focus());
      }
    },
    [conversationId, userId, economist.id, inputMode, scope],
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    void send(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const text = input.trim();
      if (text && !loading) void send(text);
    }
  }

  // V2.11 (2026-05-27 phase 3.1.c round 3) — streaming voice loop.
  // Same hook + onPartial/onFinal pattern as TwinChatPanel.
  const voiceChat = useVoiceChatLoop({
    onPartial: (t) => {
      if (!t.trim()) return;
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last?.role === 'user' && last.streaming) {
          next[next.length - 1] = { ...last, content: t };
        } else {
          next.push({ role: 'user', content: t, streaming: true, from_voice: true });
        }
        return next;
      });
    },
    onFinal: async (t, signal) => {
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last?.role === 'user' && last.streaming) {
          next[next.length - 1] = { ...last, content: t, streaming: false };
        } else {
          next.push({ role: 'user', content: t, from_voice: true });
        }
        return next;
      });
      await send(t, { fromVoice: true, skipPushUser: true, signal });
    },
    onError: (msg) => setError(msg),
    onFatalError: () => setInputMode('text'),
  });
  const voicePhase = voiceChat.phase;

  // 2026-06-26 (Leon ask) — align trial-chat voice to training:
  // batch ASR via MediaRecorder + /api/asr/transcribe instead of
  // streaming WS. Drops the WS race surface entirely.
  const recorder = useVoiceRecorder();

  // V2.11 (2026-05-28) — enter/exit voice via direct onClick to keep
  // Chrome's user-gesture stack alive for AudioContext init.
  // 2026-06-26 — batch mode skips voiceChat.enterVoice (no WS handshake).
  function handleEnterVoice() {
    setInputMode('voice');
  }
  function handleExitVoice() {
    recorder.cancel();
    setInputMode('text');
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  // 2026-06-26 — batch record handlers. Mirror session-panel:
  // start -> getUserMedia + MediaRecorder; stop -> upload -> push
  // user bubble + send to AI (no confirm card on trial chat — user
  // sees their words appear, AI replies immediately, same as text mode).
  async function handleRecorderStart() {
    try {
      await recorder.startRecording();
    } catch (e) {
      console.warn('[twin-chat-lp] recorder.startRecording threw', e);
    }
  }
  async function handleRecorderStopAndSend() {
    const text = await recorder.stopAndTranscribe();
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { role: 'user', content: trimmed, from_voice: true }]);
    await send(trimmed, { fromVoice: true, skipPushUser: true });
  }

  return (
    <div
      // V2.11 (2026-05-23) — layout is now `h-full`, so the parent
      // dictates viewport bounds. The /e/<slug> page wraps this in a
      // fixed-height column (h-[calc(100dvh-72px)] minus profile
      // header) so the input dock stays pinned to the bottom and the
      // transcript scrolls inside. Older inline `100dvh-260px` calc
      // assumed a centered single-card layout — broke after the
      // 2-pane redesign.
      //
      // V2.11 (2026-05-27) — paper-cream background (matches the test
      // chat surface at /studio/test-chat) instead of the previous
      // pure-white card. The TwinChatRail on the left already sits on
      // bg-cream/30; using bg-cream/40 here keeps the right pane in
      // the same visual family but a hair lighter to read as the
      // active chat area.
      className="grid grid-rows-[1fr_auto] sm:rounded-2xl sm:border sm:border-soft bg-cream/40 h-full min-h-[360px]"
    >
      {/* 2026-06-16 — wake hermes container on mount so the first
          consumer message doesn't pay the cold-start tax. Same trigger
          that fires on owner-preview test-chat. */}
      <HermesPrewarmTrigger />
      <div ref={scrollRef} className="px-5 py-5 overflow-y-auto text-base">
        {messages.length === 0 && (
          <>
            <p className="text-dusk text-sm m-0">
              和 <strong>{economist.display_name}</strong> 的分身随便聊。 回答自带置信度标签（
              <code className="font-mono text-xs">实时</code> /{' '}
              <code className="font-mono text-xs">历史</code> /{' '}
              <code className="font-mono text-xs">基于推断</code>）—— 这是 AI
              还原的判断，不构成投资建议。
            </p>
            {/* Amy CRO-132 A12 (2026-06-29) — sample question chips so
                first-time visitors don't stare at an empty input. Five
                question templates chosen to demonstrate the twin's range:
                macro signal (tools), past-judgment recall (vault stance),
                analytical framing (frameworks), boundary (refusal), and
                short-horizon view. Click sends as a normal user message. */}
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                '美联储下次议息你怎么看？',
                'A 股下半年配置该重哪条线？',
                '人民币汇率近期会破 7.4 吗？',
                '你最近换过什么判断？为什么？',
                '美国通胀真的回来了吗？',
              ].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    if (loading) return;
                    void send(q);
                  }}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-soft bg-white hover:bg-warmth/5 hover:border-warmth/40 text-[12px] text-ink-mute transition disabled:opacity-50"
                >
                  <span aria-hidden className="text-warmth-dark/70">
                    ›
                  </span>
                  <span>{q}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="mt-4 flex flex-col gap-3">
          {messages.map((m, i) => (
            <MessageBubble
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only, index is stable per render of a streaming chat
              key={i}
              message={m}
              onRetry={(text) => {
                // 2026-06-16 — failed-turn retry hook. Borrows the owner
                // 试聊 affordance so a busted hydrate doesn't leave the
                // user staring at an empty bubble. We just resend the
                // prior user message through the normal send pipeline;
                // the orchestrator's history filter (now orphan-aware)
                // skips the dead pair from the previous attempt.
                if (loading) return;
                void send(text, { skipPushUser: true });
              }}
              disabled={loading}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-soft px-3 py-3 bg-white sm:rounded-b-2xl">
        {error && (
          <p role="alert" className="text-warmth text-sm m-0 mb-2 px-2">
            {error}
          </p>
        )}
        <form onSubmit={onSubmit} className="flex items-end gap-2">
          {inputMode === 'text' ? (
            <>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={`问 ${economist.display_name}…`}
                disabled={loading}
                rows={1}
                className="flex-1 px-4 py-2 rounded-2xl border border-soft bg-cream/40 text-base text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition resize-none disabled:opacity-60"
                style={{ minHeight: 44, maxHeight: 140, lineHeight: '1.5' }}
              />
              {/* AXQA-44-04 — bumped to 44 (Apple HIG min). */}
              <button
                type="button"
                onClick={() => void handleEnterVoice()}
                disabled={loading}
                aria-label="切换到语音输入"
                title="语音输入"
                className="shrink-0 w-11 h-11 rounded-full border border-soft bg-white text-dusk hover:text-warmth-dark hover:border-warmth/40 transition disabled:opacity-50 flex items-center justify-center"
              >
                <Icon name="i-microphone" size={18} />
              </button>
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="shrink-0 px-4 min-h-[44px] rounded-full bg-warmth-dark text-white font-medium text-sm whitespace-nowrap hover:bg-warmth transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? '…' : '发送'}
              </button>
            </>
          ) : (
            <>
              {/* 2026-06-26 (Leon ask) — batch voice flow mirroring
                  training: 点击说话 → MediaRecorder → 结束录音 →
                  /api/asr/transcribe → push user bubble + send to AI.
                  No streaming WS, no realtime mode, no silence-auto-
                  commit. Visitor / owner both see the same flow. */}
              <div className="flex-1 flex items-center gap-2 min-h-[44px]">
                {recorder.phase === 'idle' && (
                  <button
                    type="button"
                    onClick={() => void handleRecorderStart()}
                    disabled={loading}
                    className="flex-1 h-11 rounded-full bg-warmth-dark text-white font-medium text-sm hover:bg-warmth transition inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <Icon name="i-microphone" size={16} />
                    <span>点击说话</span>
                  </button>
                )}
                {recorder.phase === 'requesting-mic' && (
                  <div className="flex-1 h-11 rounded-full border border-amber-200 bg-amber-50 inline-flex items-center justify-center gap-2 px-4">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                    <span className="font-mono text-xs text-amber-900">请求麦克风权限…</span>
                  </div>
                )}
                {recorder.phase === 'recording' && (
                  <button
                    type="button"
                    onClick={() => void handleRecorderStopAndSend()}
                    className="flex-1 h-11 rounded-full bg-rose-700 text-white font-medium text-sm hover:bg-rose-800 transition inline-flex items-center justify-center gap-2"
                  >
                    <span
                      aria-hidden
                      className="inline-block w-2.5 h-2.5 rounded-sm bg-white animate-pulse"
                    />
                    <span>结束录音 · {recorder.elapsedSec}s</span>
                  </button>
                )}
                {recorder.phase === 'transcribing' && (
                  <div className="flex-1 h-11 rounded-full border border-amber-200 bg-amber-50 inline-flex items-center justify-center gap-2 px-4">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                    <span className="font-mono text-xs text-amber-900">转录中…</span>
                  </div>
                )}
                {recorder.phase === 'error' && (
                  <div className="flex-1 h-11 rounded-full border border-rose-200 bg-rose-50 inline-flex items-center gap-2 px-4">
                    <span className="font-mono text-xs text-rose-700 flex-1 text-center">
                      {recorder.error ?? '语音失败'}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleExitVoice}
                aria-label="切换到文字输入"
                title="文字输入"
                className="shrink-0 w-11 h-11 rounded-full border border-soft bg-white text-dusk hover:text-warmth-dark hover:border-warmth/40 transition flex items-center justify-center"
              >
                ⌨︎
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onRetry,
  disabled,
}: {
  message: ChatMessage;
  /** 2026-06-16 — borrows the owner 试聊 (TwinChatBubble) failed-bubble
   *  affordance. When set + message.failed + empty content, the bubble
   *  renders as a「⚠ AI 没回复 · 重试」 pill that resends the prior user
   *  turn. Without this, hydrate emitted blank bubbles for the dead
   *  rows and the user couldn't tell what happened. */
  onRetry?: (text: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const isUser = message.role === 'user';
  // V2.11 (2026-05-27 phase 3.1.c round 3) — streaming voice messages
  // render as plain text (not the collapsed 🎤 widget) so the partial
  // transcript appears flat in the bubble as it's being typed by ASR.
  // Once the bubble is finalised, we keep showing the content plain
  // too — collapsing into a 🎤 details widget on every voice turn was
  // friction for the new live-transcription flow.
  const isVoice = isUser && message.from_voice === true && !message.streaming && false;
  // 2026-06-16 — failed-empty assistant gets the 「AI 没回复 · 重试」 pill
  // instead of the normal bubble shell. Mirrors twin-chat-v2 owner-side
  // FailedBubble so the visual treatment is consistent across owner and
  // consumer surfaces.
  const showFailed = !isUser && message.failed && (message.content ?? '').trim().length === 0;
  if (showFailed) {
    const retryText = message.retry_text ?? '';
    return (
      <div className="flex justify-start">
        <output className="inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-cream/70 border border-soft text-ink">
          <span aria-hidden className="text-warmth-dark text-base leading-none">
            ⚠
          </span>
          <span className="text-[14px]">AI 没回复</span>
          {retryText && onRetry && (
            <button
              type="button"
              onClick={() => onRetry(retryText)}
              disabled={!!disabled}
              className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full bg-warmth-dark text-white hover:bg-warmth-dark/90 disabled:opacity-50 transition"
            >
              <span aria-hidden>↻</span>
              <span>重试</span>
            </button>
          )}
        </output>
      </div>
    );
  }
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 leading-relaxed break-words rounded-2xl ${
          isUser
            ? 'bg-warmth-dark text-white rounded-br-sm'
            : // V2.11 (2026-05-27) — assistant bubble switched to white
              // now that the surrounding panel is bg-cream/40 (was
              // white). Border kept for definition against the cream.
              'bg-white border border-soft text-ink rounded-bl-sm'
        } ${isVoice ? 'pb-2' : 'whitespace-pre-wrap'}`}
      >
        {isVoice ? (
          <details className="group">
            <summary className="flex items-center gap-2 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
              <Icon name="i-microphone" size={16} />
              <span className="text-[15px] font-medium">语音</span>
              <span className="text-xs opacity-80 group-open:hidden">· 点开查看转写</span>
              <span className="text-xs opacity-80 hidden group-open:inline">· 收起</span>
            </summary>
            <div className="mt-2 pt-2 border-t border-white/20 text-[15px] whitespace-pre-wrap text-white/90">
              {message.content}
            </div>
          </details>
        ) : message.streaming && message.content === '' ? (
          // V2.11 (2026-05-23) — pre-first-token "正在思考…" indicator
          // parity with ChatShell + TwinChatPanel.
          <ThinkingIndicator startedAt={message.streaming_started_at ?? Date.now()} />
        ) : (
          // Amy CRO-132 P1-2 (2026-06-29) — strip raw [F1][C2] citation
          // tokens before display. On owner-preview those render as
          // clickable footnote chips into the evidence drawer; on the
          // public twin we collapse them out (visitor sees the summary
          // chip below the message instead).
          <span className="whitespace-pre-wrap">
            {isUser ? message.content : stripTechLeak(stripCitations(message.content))}
          </span>
        )}
        {message.streaming && message.content !== '' && (
          <span aria-label="正在输入" className="text-dusk ml-1">
            …
          </span>
        )}
      </div>
      {!isUser && !message.streaming && isBoundaryRefusal(message.content) && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-soft bg-cream/40 text-text-mute text-[11px] max-w-[85%]">
          <span aria-hidden>🛑</span>
          <span>边界拒答 · 未使用证据</span>
        </div>
      )}
      {!isUser &&
        !message.streaming &&
        !isBoundaryRefusal(message.content) &&
        detectUnsourcedRealtimeQuote(message.content, message.memory) && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-warmth/40 bg-warmth/10 text-warmth-dark text-[11px] max-w-[85%]">
            <span aria-hidden>⚠️</span>
            <span>这条回复含实时数据估值，但没拉到当前行情。请以官方报价为准。</span>
          </div>
        )}
      {!isUser && !message.streaming && !isBoundaryRefusal(message.content) && message.memory && (
        <PublicEvidenceStrip memory={message.memory} />
      )}
    </div>
  );
}

/**
 * Amy CRO-132 P1-2 (2026-06-29) — public-twin evidence strip.
 *
 * Below the assistant bubble we surface a compact pill summarising what
 * grounded this answer. Click expands a tidy list: concept names with
 * origin chips (本人 / 第三方融合 / 外部), framework names, vault titles.
 * Deliberately leaner than the owner-preview drawer (no debug score /
 * cross-domain badges / [F1][C2] index chips) since the visitor doesn't
 * have access to the underlying records.
 */
function PublicEvidenceStrip({ memory }: { memory: PublicMemory }): JSX.Element | null {
  const total =
    memory.concepts.length +
    memory.skills.length +
    memory.vault_passages.length +
    memory.external_sources.length;
  if (total === 0) return null;
  return (
    <details className="self-start mt-1.5 max-w-[85%] ml-[2px]">
      <summary className="cursor-pointer text-[11px] text-dusk hover:text-ink select-none inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-soft bg-cream/70 hover:bg-cream transition">
        <span aria-hidden>🔎</span>
        <span>
          基于 {memory.concepts.length} 个观点 · {memory.skills.length} 个思维框架 ·{' '}
          {memory.vault_passages.length} 篇原文 · {memory.external_sources.length} 个外部数据源
        </span>
      </summary>
      <div className="mt-1.5 bg-cream/40 border border-soft rounded-lg p-3 text-[12px] space-y-2.5 text-ink">
        {memory.concepts.length > 0 && (
          <div>
            <p className="text-text-mute uppercase tracking-wider text-[10px] mb-1">观点</p>
            <ul className="space-y-1">
              {memory.concepts.map((c) => {
                const tag =
                  c.origin === 'self'
                    ? { label: '本人原创', bg: 'bg-sage/15 text-sage-dark' }
                    : c.origin === 'merged'
                      ? { label: '第三方融合', bg: 'bg-gold/15 text-gold-dark' }
                      : c.origin === 'external'
                        ? { label: '外部', bg: 'bg-soft/60 text-text-mute' }
                        : null;
                return (
                  <li key={c.slug} className="leading-snug">
                    <span>{c.display_name}</span>
                    {tag && (
                      <span
                        className={`ml-1.5 inline-flex items-center px-1.5 py-px rounded text-[9px] font-mono ${tag.bg}`}
                      >
                        {tag.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {memory.skills.length > 0 && (
          <div>
            <p className="text-text-mute uppercase tracking-wider text-[10px] mb-1">思维框架</p>
            <ul className="space-y-1">
              {memory.skills.map((s) => (
                <li key={s.name} className="leading-snug">
                  <span className="font-medium">{s.name}</span>
                  {s.description && (
                    <span className="block pl-3 text-dusk text-[11px]">{s.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {memory.vault_passages.length > 0 && (
          <div>
            <p className="text-text-mute uppercase tracking-wider text-[10px] mb-1">原文材料</p>
            <ul className="space-y-1">
              {memory.vault_passages.map((v, i) => (
                <li key={`${v.title}-${i}`} className="leading-snug">
                  <span>{v.title}</span>
                  {(v.publisher || v.date) && (
                    <span className="ml-1 text-text-mute text-[10px]">
                      ·{v.publisher ? ` ${v.publisher}` : ''}
                      {v.date ? ` · ${v.date.slice(0, 10)}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {memory.external_sources.length > 0 && (
          <div>
            <p className="text-text-mute uppercase tracking-wider text-[10px] mb-1">外部数据</p>
            <ul className="space-y-1">
              {memory.external_sources.map((source) => (
                <li key={source.name} className="leading-snug">
                  <span>{source.name.replaceAll('__', ' · ')}</span>
                  {(source.provider || source.date) && (
                    <span className="ml-1 text-text-mute text-[10px]">
                      {source.provider ? `· ${source.provider}` : ''}
                      {source.date ? ` · ${source.date.slice(0, 10)}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

function ThinkingIndicator({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <span className="inline-flex items-center gap-2 text-dusk text-[15px] italic">
      <span aria-hidden className="inline-flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce" />
      </span>
      <span>正在思考…</span>
      <span className="text-text-mute text-[13px] not-italic tabular-nums">({elapsedSec} 秒)</span>
    </span>
  );
}
